/**
 * Listening interface component.
 *
 * Main component for the listening exam module. Displays questions and notes area.
 * Audio playback is fully automatic - section title audio and context audios play
 * sequentially without manual intervention. Sections auto-advance after all audios complete.
 *
 * @module ListeningInterface
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ExamHeader } from "@/components/exam/ExamHeader";
import { ListeningContextPanel } from "@/components/exam/ListeningContextPanel";
import { QuestionPanel } from "@/components/exam/QuestionPanel";
import {
  ResizableModulePanels,
  SimulationChunkNavigation,
  SimulationModuleError,
  SimulationModuleLoading,
  SimulationSavingOverlay,
} from "@/components/exam/shared";
import { useListeningModuleState } from "@/hooks/useListeningModuleState";
import { useAudioPlayback } from "@/hooks/useAudioPlayback";
import { useAudioAnalyser } from "@/hooks/useAudioAnalyser";
import { useReviewPeriod } from "@/hooks/useReviewPeriod";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { formatSimulationSectionTitle } from "@/utils/simulationSectionTitle";
import {
  incompleteModuleDialogTitle,
  submitModuleButtonLabel,
} from "@/lib/simulationSubmitModuleLabels";
import { REVIEW_TIME_SECONDS } from "@/lib/constants";
import type {
  CEFRLevel,
  ExamType,
  SimulationSettings,
} from "@/types/exam-simulation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";
import { playExamTimerWarningSound } from "@/utils/examTimerWarningSound";
import {
  buildMcqNavigatorItems,
  filterUnansweredItems,
} from "@/utils/simulationQuestionNavigation";
import { SimulationUnansweredJumpChips } from "@/components/exam/SimulationUnansweredJumpChips";
import { useSimulationInactivityMonitor } from "@/hooks/useSimulationInactivityMonitor";
import { SIMULATION_INACTIVITY_TIMEOUT_MS } from "@/lib/simulationRuntimeConfig";
import { SimulationInactivityDialog } from "@/components/exam/SimulationInactivityDialog";
import { useIsLargeScreen } from "@/hooks/use-large-screen";

interface ListeningInterfaceProps {
  readonly examType: ExamType;
  readonly level: CEFRLevel;
  readonly settings: SimulationSettings | null;
  readonly onModuleFinish?: () => void;
  /** Session ID shared across all modules in a simulation */
  readonly sessionId?: string | null;
}

export function ListeningInterface({
  examType,
  level,
  settings,
  onModuleFinish,
  sessionId,
}: ListeningInterfaceProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const {
    sections,
    allQuestions,
    currentSection,
    currentSectionQuestions,
    currentSectionAudioQueue,
    isLoadingData,
    dataError,
    state,
    moduleTimeRemaining,
    handleAnswerSelect,
    handleStrikeToggle,
    handleFlagToggle,
    handleQuestionSelect,
    jumpToQuestion,
    handleSectionChange,
    handleNotesChange,
    handleFinishModule,
    handleSectionTitleAudioComplete,
    handleContextAudioComplete,
  } = useListeningModuleState({
    examType,
    level,
    settings,
    userId: user?.id,
    sessionId,
  });

  const { isAudioPlaying, activeAudioElement } = useAudioPlayback({
    audioQueue: currentSectionAudioQueue,
    isPlayingSectionTitleAudio: state.isPlayingSectionTitleAudio,
    sectionTitleAudioCompleted: state.sectionTitleAudioCompleted,
    currentContextAudioIndex: state.currentContextAudioIndex,
    completedContextAudioIndices: state.completedContextAudioIndices,
    isReviewPeriod: state.isReviewPeriod,
    onSectionTitleAudioComplete: handleSectionTitleAudioComplete,
    onContextAudioComplete: handleContextAudioComplete,
  });

  // Drive the listening waveform with the real playing audio (flat on silence)
  const { readInto: readAudioLevels } = useAudioAnalyser({
    mediaElement: activeAudioElement,
    enabled: isAudioPlaying,
  });

  const totalParts = sections.length;

  // Mobile tab order: context | questions (shared with Writing's prompt | task ordering);
  // the default active tab remains `questions` so learners land on the primary task.
  const [activeTab, setActiveTab] = useState<"questions" | "context">(
    "questions",
  );
  const isLargeScreen = useIsLargeScreen();
  const inactivity = useSimulationInactivityMonitor({
    enabled: !isSaving && allQuestions.length > 0,
    timeoutMs: SIMULATION_INACTIVITY_TIMEOUT_MS,
  });

  // Play the warning chime exactly once when the review period starts.
  // The ExamHeader's auto-beep is disabled for the 'listening' variant so only
  // this effect fires, at the precise review-start transition.
  const hasPlayedReviewBeepRef = useRef(false);
  useEffect(() => {
    if (state.isReviewPeriod && !hasPlayedReviewBeepRef.current) {
      hasPlayedReviewBeepRef.current = true;
      playExamTimerWarningSound();
    }
  }, [state.isReviewPeriod]);

  const mcqNavigatorItems = useMemo(
    () =>
      allQuestions.length > 0
        ? buildMcqNavigatorItems(
            allQuestions,
            state.selectedAnswers,
            state.flaggedQuestions,
          )
        : [],
    [allQuestions, state.selectedAnswers, state.flaggedQuestions],
  );

  const unansweredNavItems = useMemo(
    () => filterUnansweredItems(mcqNavigatorItems),
    [mcqNavigatorItems],
  );

  const questionNavigator = useMemo(
    () =>
      state.isReviewPeriod && mcqNavigatorItems.length > 0
        ? {
            items: mcqNavigatorItems,
            onJump: (id: string) => {
              jumpToQuestion(Number(id));
              setActiveTab("questions");
            },
          }
        : null,
    [state.isReviewPeriod, mcqNavigatorItems, jumpToQuestion],
  );

  const handleSaveAndFinish = useCallback(async () => {
    if (isSaving || allQuestions.length === 0) {
      return;
    }

    setIsSaving(true);

    try {
      // Save all answers and result
      await handleFinishModule();

      // Call onModuleFinish callback after successful save
      if (onModuleFinish) {
        onModuleFinish();
      }
    } catch (error) {
      logger.error("Error saving answers", error);
      toast.error(
        t("simulation.errors.saveFailed", {
          defaultValue:
            "Failed to save your answers. Please try again or contact support if the problem persists.",
        }),
      );
      setIsSaving(false);
    }
  }, [allQuestions, handleFinishModule, isSaving, onModuleFinish, t]);

  // Review period auto-finish
  useReviewPeriod({
    isReviewPeriod: state.isReviewPeriod,
    timeRemaining: state.timeRemaining,
    onExpire: () => {
      if (!isSaving) {
        handleSaveAndFinish();
      }
    },
  });

  const handleFinish = async () => {
    if (allQuestions.length === 0) {
      if (onModuleFinish) {
        onModuleFinish();
      }
      return;
    }

    if (unansweredNavItems.length > 0) {
      setShowConfirmDialog(true);
    } else {
      await handleSaveAndFinish();
    }
  };

  const handleConfirmFinish = async () => {
    setShowConfirmDialog(false);
    await handleSaveAndFinish();
  };

  const handleReviewUnanswered = () => {
    setShowConfirmDialog(false);
    const firstUnanswered = unansweredNavItems[0];
    if (firstUnanswered) {
      jumpToQuestion(Number(firstUnanswered.id));
      setActiveTab("questions");
    }
  };

  const moduleDisplayName = t("simulation.instructions.modules.listening", {
    defaultValue: "Listening",
  });
  const sectionTitle = formatSimulationSectionTitle(
    examType,
    level,
    moduleDisplayName,
  );
  const reviewMessage = t("simulation.active.reviewMessage", {
    minutes: REVIEW_TIME_SECONDS / 60,
    defaultValue: "You have {{minutes}} minutes to review the given answers.",
  });

  if (isLoadingData) {
    return (
      <SimulationModuleLoading
        message={t("simulation.errors.loadingModule", {
          module: "Listening",
          defaultValue: "Loading listening module...",
        })}
      />
    );
  }

  if (dataError) {
    return (
      <SimulationModuleError
        message={t("simulation.errors.loadFailure", {
          moduleName: "Listening",
          attempts: 1,
          errorMessage: "Unknown",
          defaultValue: "Failed to load listening module data",
        })}
        detail={
          dataError instanceof Error
            ? dataError.message
            : t("simulation.errors.unknownError", {
                defaultValue: "Unknown error occurred",
              })
        }
      />
    );
  }

  const contextPanel = (
    <ListeningContextPanel
      sectionTitle={
        currentSection?.title ||
        t("simulation.active.audioPlayer", { defaultValue: "Audio Player" })
      }
      isReviewPeriod={state.isReviewPeriod}
      reviewMessage={reviewMessage}
      isAudioPlaying={isAudioPlaying}
      getAudioLevels={readAudioLevels}
      notes={state.notes}
      onNotesChange={handleNotesChange}
    />
  );

  const questionPanel = (
    <QuestionPanel
      questions={currentSectionQuestions}
      activeQuestionId={state.activeQuestionId}
      selectedAnswers={state.selectedAnswers}
      struckAnswers={state.struckAnswers}
      flaggedQuestions={state.flaggedQuestions}
      totalQuestions={allQuestions.length}
      currentIndex={state.currentSectionIndex}
      onQuestionSelect={handleQuestionSelect}
      onAnswerSelect={handleAnswerSelect}
      onStrikeToggle={handleStrikeToggle}
      onFlagToggle={handleFlagToggle}
      questionNavigator={questionNavigator}
    />
  );

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-background">
      <ExamHeader
        sectionTitle={sectionTitle}
        currentChunk={state.currentSectionIndex}
        totalChunks={totalParts}
        timeRemaining={
          state.isReviewPeriod ? state.timeRemaining : moduleTimeRemaining
        }
        onFinish={handleFinish}
        disabled={isSaving}
        timerVariant={
          state.isReviewPeriod
            ? "review"
            : moduleTimeRemaining > 0
              ? "listening"
              : "inactive"
        }
        timerNote={null}
        chunkLabel={t("simulation.active.chunkLabel.part", {
          defaultValue: "Part",
        })}
        finishModule="listening"
      />

      {/* Mobile View Toggle (Tabs): Audio context first, Questions second to match Writing's tab ordering */}
      <div className="lg:hidden flex border-b bg-muted/30 shrink-0">
        <button
          className={cn(
            "flex-1 py-3 font-ui text-sm font-semibold transition-colors border-b-2",
            activeTab === "context"
              ? "border-primary text-primary bg-background"
              : "border-transparent text-muted-foreground",
          )}
          onClick={() => setActiveTab("context")}
        >
          {t("simulation.active.audioAndNotes", {
            defaultValue: "Audio & Notes",
          })}
        </button>
        <button
          className={cn(
            "flex-1 py-3 font-ui text-sm font-semibold transition-colors border-b-2",
            activeTab === "questions"
              ? "border-primary text-primary bg-background"
              : "border-transparent text-muted-foreground",
          )}
          onClick={() => setActiveTab("questions")}
        >
          {t("simulation.active.questions", { defaultValue: "Questions" })}
        </button>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {isLargeScreen ? (
          <ResizableModulePanels
            autoSaveId="simulation-listening-horizontal-panels"
            left={contextPanel}
            right={questionPanel}
            leftDefaultSize={40}
            rightDefaultSize={60}
            leftMinSize={30}
            rightMinSize={35}
          />
        ) : (
          <>
            <div
              className={cn(
                "overflow-hidden",
                activeTab === "context" ? "flex-1 flex flex-col" : "hidden",
              )}
            >
              {contextPanel}
            </div>
            <div
              className={cn(
                "overflow-hidden",
                activeTab === "questions" ? "flex-1 flex flex-col" : "hidden",
              )}
            >
              {questionPanel}
            </div>
          </>
        )}
      </div>

      {state.isReviewPeriod ? (
        <SimulationChunkNavigation
          currentIndex={state.currentSectionIndex}
          totalChunks={totalParts}
          onPrevious={() => handleSectionChange(state.currentSectionIndex - 1)}
          onNext={() => handleSectionChange(state.currentSectionIndex + 1)}
        />
      ) : null}

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {incompleteModuleDialogTitle(t, "listening")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("simulation.active.unansweredQuestionsConsequence", {
                count: unansweredNavItems.length,
                module: moduleDisplayName,
                defaultValue:
                  "These {{count}} unanswered responses will be marked as not answered and counted against your score. Are you sure you want to submit the {{module}} module?",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <SimulationUnansweredJumpChips
            items={unansweredNavItems}
            disabled={isSaving}
            navigationEnabled={state.isReviewPeriod}
            onJump={(id) => {
              setShowConfirmDialog(false);
              jumpToQuestion(Number(id));
              setActiveTab("questions");
            }}
          />
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={handleReviewUnanswered}
              disabled={isSaving}
            >
              {t("simulation.active.reviewQuestions", {
                defaultValue: "Review Questions",
              })}
            </AlertDialogAction>
            <AlertDialogAction
              className="border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
              onClick={handleConfirmFinish}
              disabled={isSaving}
            >
              {isSaving
                ? t("common.saving", { defaultValue: "Saving..." })
                : submitModuleButtonLabel(t, "listening")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SimulationInactivityDialog
        open={inactivity.isPromptOpen}
        timeRemainingSeconds={state.timeRemaining}
        onContinue={inactivity.continueSession}
        onEnd={handleSaveAndFinish}
      />

      <SimulationSavingOverlay open={isSaving} />
    </div>
  );
}
